import React, { ChangeEventHandler, useCallback, useEffect, useRef } from 'react';
import 'intl-tel-input/build/css/intlTelInput.css';
import '@assets/css/intlTelInput.css';
import intlTelInput from 'intl-tel-input';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';

// copied from the library
enum PhoneErrorCodes {
  IS_POSSIBLE = 0,
  INVALID_COUNTRY_CODE = 1,
  TOO_SHORT = 2,
  TOO_LONG = 3,
  IS_POSSIBLE_LOCAL_ONLY = 4,
  INVALID_LENGTH = 5,
}

const PhoneErrorMessages = {
  [PhoneErrorCodes.IS_POSSIBLE]: 'errors.invalid-phone-number',
  [PhoneErrorCodes.INVALID_COUNTRY_CODE]: 'errors.invalid-phone-number',
  [PhoneErrorCodes.TOO_SHORT]: 'errors.too-short',
  [PhoneErrorCodes.TOO_LONG]: 'errors.too-long',
  [PhoneErrorCodes.IS_POSSIBLE_LOCAL_ONLY]: 'errors.invalid-phone-number',
  [PhoneErrorCodes.INVALID_LENGTH]: 'errors.invalid-phone-number',
};

interface PhoneInnerValidationResult {
  isValidPhone: boolean;
  phoneErrorMessage: string;
}

interface PhoneInputProps {
  className?: string;
  name?: string;
  label?: string;
  helperText?: string | React.ReactElement;
  errorText?: string;
  value?: string;
  isDisabled?: boolean;
  onChange: ChangeEventHandler;
  setPhoneInnerValidationResult?: (validationResult: PhoneInnerValidationResult) => void;
  autoFocus?: boolean;
}

export const usePhoneInnerValidation = (phoneNumber: string = '') => {
  const phoneInnerValidationResult = useRef<PhoneInnerValidationResult>({
    isValidPhone: Boolean(phoneNumber),
    phoneErrorMessage: 'errors.invalid-phone-number',
  });

  const setPhoneInnerValidationResult = useCallback((validationResult: PhoneInnerValidationResult): void => {
    phoneInnerValidationResult.current = validationResult;
  }, []);

  const getPhoneInnerValidationResult = useCallback(() => phoneInnerValidationResult.current, []);

  return {
    setPhoneInnerValidationResult,
    getPhoneInnerValidationResult,
  };
};

export const PhoneInput = (props: PhoneInputProps) => {
  const {
    className,
    helperText = '',
    errorText = '',
    value,
    isDisabled = false,
    onChange,
    setPhoneInnerValidationResult,
    autoFocus,
  } = props;

  const inputRef = useRef<HTMLInputElement>(null);
  const itiRef = useRef<any>(null);
  const { t } = useTranslation();

  useEffect(() => {
    if (inputRef.current) {
      itiRef.current = intlTelInput(inputRef.current, {
        utilsScript: '/utils.js',
        placeholderNumberType: 'MOBILE',
        separateDialCode: true,
        nationalMode: true,
        formatOnDisplay: false,
        customContainer: 'flex w-full h-full',
        initialCountry: 'ua',
        preferredCountries: ['ua', 'gb', 'pl', 'de', 'fr'],
        customPlaceholder: (selectedCountryPlaceholder: string) => {
          return selectedCountryPlaceholder.replace(/[1-9]/g, '0');
        },
      });

      if (autoFocus) {
        inputRef.current.focus();
      }
    }

    return () => {
      itiRef.current?.destroy();
    };
  }, []);

  useEffect(() => {
    itiRef.current?.setNumber(value);
  }, [value]);

  const onChangeInner = (event: any) => {
    const number = event.target.value;

    itiRef.current?.setNumber(number);

    const intlNumber = itiRef.current?.getNumber();

    const isValidPhone: boolean = itiRef.current?.isValidNumber();

    const phoneErrorCode: PhoneErrorCodes = itiRef.current?.getValidationError();
    const phoneErrorMessage: string = PhoneErrorMessages[phoneErrorCode] ?? 'errors.invalid-phone-number';

    if (typeof setPhoneInnerValidationResult === 'function') {
      setPhoneInnerValidationResult({
        isValidPhone,
        phoneErrorMessage: isValidPhone ? '' : phoneErrorMessage,
      });
    }

    if (typeof onChange === 'function') {
      onChange(intlNumber);
    }
  };

  return (
    <>
      <div
        className={clsx(
          'relative w-full h-[50px] border-b-[1px] border-b-solid',
          'hover:bg-black-7 focus-within:bg-black-7',
          errorText ? 'border-b-carnation' : 'border-b-black',
          className,
        )}
      >
        <input
          type="tel"
          ref={inputRef}
          className={clsx(
            'peer relative w-full h-full py-3 !pr-4 !pl-0 outline-none bg-transparent text-base text-black transition-all',
            'placeholder:text-black-50',
          )}
          disabled={isDisabled}
          autoFocus={autoFocus}
          onChange={onChangeInner}
        />
        {(errorText || helperText) && (
          <span
            className={clsx(
              'absolute left-0 top-full mt-[5px] px-4 text-xxs',
              errorText && 'text-carnation',
              !errorText && helperText && 'text-black-50',
            )}
          >
            {t(errorText) || helperText}
          </span>
        )}
      </div>
    </>
  );
};
